// Theme catalog — admin selectable hospital-wide palettes.
// Each palette must define every CSS variable below in "R G B" space-separated format
// so Tailwind can apply opacity modifiers via rgb(var(--brand-teal) / <alpha-value>).

const HOSPITAL_THEMES = {
  'teal-navy': {
    label_en: 'Teal & Navy',
    label_hi: 'टील और नेवी',
    description_en: 'Clean medical (logo-derived)',
    swatch: ['#0F766E', '#0A1F44', '#FAFAFA'],
    palette: {
      'brand-teal': '15 118 110',
      'brand-teal-dark': '13 91 91',
      'brand-teal-light': '94 234 212',
      'brand-teal-tint': '240 249 248',
      'brand-navy': '10 31 68',
      'brand-navy-dark': '4 14 38',
      'brand-navy-tint': '232 237 245',
      'brand-emergency': '234 88 12',
      'brand-sand': '250 250 250',
    },
  },
  'monochrome': {
    label_en: 'Monochrome',
    label_hi: 'मोनोक्रोम',
    description_en: 'Black & white minimalist',
    swatch: ['#000000', '#FFFFFF', '#F4F4F5'],
    palette: {
      'brand-teal': '17 17 17',
      'brand-teal-dark': '0 0 0',
      'brand-teal-light': '212 212 216',
      'brand-teal-tint': '244 244 245',
      'brand-navy': '0 0 0',
      'brand-navy-dark': '0 0 0',
      'brand-navy-tint': '244 244 245',
      'brand-emergency': '220 38 38',
      'brand-sand': '255 255 255',
    },
  },
  'royal-blue-gold': {
    label_en: 'Royal Blue & Gold',
    label_hi: 'रॉयल ब्लू और गोल्ड',
    description_en: 'Premium hospital',
    swatch: ['#1E3A8A', '#D4A017', '#FFFBEB'],
    palette: {
      'brand-teal': '212 160 23',         // gold primary
      'brand-teal-dark': '161 122 8',
      'brand-teal-light': '253 224 71',
      'brand-teal-tint': '255 251 235',
      'brand-navy': '30 58 138',          // royal blue
      'brand-navy-dark': '23 37 84',
      'brand-navy-tint': '224 231 255',
      'brand-emergency': '220 38 38',
      'brand-sand': '255 251 235',
    },
  },
  'forest-cream': {
    label_en: 'Forest & Cream',
    label_hi: 'फॉरेस्ट और क्रीम',
    description_en: 'Calm & organic',
    swatch: ['#166534', '#1F2937', '#FFFBEB'],
    palette: {
      'brand-teal': '22 101 52',          // forest green
      'brand-teal-dark': '20 83 45',
      'brand-teal-light': '134 239 172',
      'brand-teal-tint': '240 253 244',
      'brand-navy': '31 41 55',           // charcoal slate
      'brand-navy-dark': '17 24 39',
      'brand-navy-tint': '249 250 251',
      'brand-emergency': '194 65 12',
      'brand-sand': '254 252 232',
    },
  },
  'midnight-mint': {
    label_en: 'Midnight & Mint',
    label_hi: 'मिडनाइट और मिंट',
    description_en: 'Cool corporate',
    swatch: ['#0F172A', '#10B981', '#F8FAFC'],
    palette: {
      'brand-teal': '16 185 129',         // emerald
      'brand-teal-dark': '5 150 105',
      'brand-teal-light': '110 231 183',
      'brand-teal-tint': '236 253 245',
      'brand-navy': '15 23 42',           // midnight
      'brand-navy-dark': '2 6 23',
      'brand-navy-tint': '241 245 249',
      'brand-emergency': '244 63 94',
      'brand-sand': '248 250 252',
    },
  },
  'burgundy-rose': {
    label_en: 'Burgundy & Rose',
    label_hi: 'बरगंडी और रोज़',
    description_en: 'Warm hospitality',
    swatch: ['#7F1D1D', '#F59E0B', '#FFF7ED'],
    palette: {
      'brand-teal': '190 18 60',          // rose-burgundy primary
      'brand-teal-dark': '136 19 55',
      'brand-teal-light': '252 165 165',
      'brand-teal-tint': '255 241 242',
      'brand-navy': '127 29 29',          // deep burgundy
      'brand-navy-dark': '69 10 10',
      'brand-navy-tint': '255 247 237',
      'brand-emergency': '245 158 11',
      'brand-sand': '255 247 237',
    },
  },
};

// Blood Bank themes — entity is "different from the hospital", red-led
const BLOOD_BANK_THEMES = {
  'blood-red': {
    label_en: 'Blood Red & Teal',
    label_hi: 'ब्लड रेड और टील',
    description_en: 'Bold complementary',
    swatch: ['#B91C1C', '#0F766E', '#FFFBEB'],
    palette: {
      'brand-teal': '185 28 28',          // crimson primary
      'brand-teal-dark': '127 29 29',
      'brand-teal-light': '252 165 165',
      'brand-teal-tint': '254 242 242',
      'brand-navy': '127 29 29',          // deep blood
      'brand-navy-dark': '69 10 10',
      'brand-navy-tint': '254 226 226',
      'brand-emergency': '15 118 110',    // teal as accent (complementary)
      'brand-sand': '255 251 235',
    },
  },
  'crimson-cream': {
    label_en: 'Crimson & Cream',
    label_hi: 'क्रिमसन और क्रीम',
    description_en: 'Classic donation',
    swatch: ['#DC2626', '#FEF3C7', '#FFFBEB'],
    palette: {
      'brand-teal': '220 38 38',
      'brand-teal-dark': '185 28 28',
      'brand-teal-light': '254 202 202',
      'brand-teal-tint': '254 252 232',
      'brand-navy': '127 29 29',
      'brand-navy-dark': '69 10 10',
      'brand-navy-tint': '254 226 226',
      'brand-emergency': '217 119 6',
      'brand-sand': '255 251 235',
    },
  },
  'crimson-rosegold': {
    label_en: 'Crimson & Rose Gold',
    label_hi: 'क्रिमसन और रोज़ गोल्ड',
    description_en: 'Premium care',
    swatch: ['#9F1239', '#F59E0B', '#FFFBEB'],
    palette: {
      'brand-teal': '159 18 57',
      'brand-teal-dark': '107 12 38',
      'brand-teal-light': '253 164 175',
      'brand-teal-tint': '255 241 242',
      'brand-navy': '107 12 38',
      'brand-navy-dark': '67 8 24',
      'brand-navy-tint': '255 228 230',
      'brand-emergency': '245 158 11',
      'brand-sand': '255 251 235',
    },
  },
};

// Diagnostic Centre — clinical, data-driven cool tones
const DIAGNOSTIC_THEMES = {
  'diagnostic-cyan': {
    label_en: 'Cyan & Slate',
    label_hi: 'सायन और स्लेट',
    description_en: 'Cool clinical',
    swatch: ['#0891B2', '#1E293B', '#F0F9FF'],
    palette: {
      'brand-teal': '8 145 178',          // cyan-600
      'brand-teal-dark': '14 116 144',
      'brand-teal-light': '103 232 249',
      'brand-teal-tint': '236 254 255',
      'brand-navy': '30 41 59',           // slate-800
      'brand-navy-dark': '15 23 42',
      'brand-navy-tint': '241 245 249',
      'brand-emergency': '234 88 12',
      'brand-sand': '248 250 252',
    },
  },
  'diagnostic-indigo': {
    label_en: 'Indigo & Sky',
    label_hi: 'इंडिगो और स्काई',
    description_en: 'Deep diagnostics',
    swatch: ['#4338CA', '#0EA5E9', '#EEF2FF'],
    palette: {
      'brand-teal': '67 56 202',          // indigo-700
      'brand-teal-dark': '55 48 163',
      'brand-teal-light': '165 180 252',
      'brand-teal-tint': '238 242 255',
      'brand-navy': '49 46 129',
      'brand-navy-dark': '30 27 75',
      'brand-navy-tint': '224 231 255',
      'brand-emergency': '234 88 12',
      'brand-sand': '248 250 252',
    },
  },
};

export const ALL_THEMES = {
  ...HOSPITAL_THEMES,
  ...BLOOD_BANK_THEMES,
  ...DIAGNOSTIC_THEMES,
};

export const HOSPITAL_THEME_KEYS = Object.keys(HOSPITAL_THEMES);
export const BLOOD_BANK_THEME_KEYS = Object.keys(BLOOD_BANK_THEMES);
export const DIAGNOSTIC_THEME_KEYS = Object.keys(DIAGNOSTIC_THEMES);

export const HOSPITAL_THEME_LIST = HOSPITAL_THEME_KEYS.map(k => ({ key: k, ...HOSPITAL_THEMES[k] }));
export const BLOOD_BANK_THEME_LIST = BLOOD_BANK_THEME_KEYS.map(k => ({ key: k, ...BLOOD_BANK_THEMES[k] }));
export const DIAGNOSTIC_THEME_LIST = DIAGNOSTIC_THEME_KEYS.map(k => ({ key: k, ...DIAGNOSTIC_THEMES[k] }));

export function getTheme(key, fallback = 'teal-navy') {
  return ALL_THEMES[key] || ALL_THEMES[fallback];
}

// Apply a theme's palette as CSS variables to a target element
// (defaults to <html> root). Returns the previous values so callers can restore.
export function applyThemePalette(themeKey, target = null) {
  const theme = getTheme(themeKey);
  const el = target || document.documentElement;
  const previous = {};
  Object.entries(theme.palette).forEach(([cssVar, value]) => {
    previous[cssVar] = el.style.getPropertyValue(`--${cssVar}`);
    el.style.setProperty(`--${cssVar}`, value);
  });
  return previous;
}

export default { ALL_THEMES, HOSPITAL_THEMES, BLOOD_BANK_THEMES, DIAGNOSTIC_THEMES };
